import { SaleOffer, BuyRequest } from '../models/Marketplace';
import { User } from '../models/User';
import { getAllUsers, getDisplayName } from '../data/userDatabase.ts';

// Type guards for determining item types
export const isSaleOffer = (item: SaleOffer | BuyRequest): item is SaleOffer => {
  return 'seller' in item && 'timestamp' in item;
};

export const isBuyRequest = (item: SaleOffer | BuyRequest): item is BuyRequest => {
  return 'requester' in item && 'requestTimestamp' in item;
};

// Generic data extraction functions
export const getPersonId = (item: SaleOffer | BuyRequest): string => {
  return isSaleOffer(item) ? item.seller : item.requester;
};

export const getPersonDisplayName = async (item: SaleOffer | BuyRequest): Promise<string> => {
  const userIdOrUsername = getPersonId(item);
  
  try {
    const users = await getAllUsers();
    
    // Find user by ID
    const user = users.find(u => u.id === userIdOrUsername);
    
    if (user) {
      return getDisplayName(user);
    } else {
      return `User ${userIdOrUsername}`;
    }
  } catch (error) {
    console.error('Error getting person display name:', error);
    return `User ${userIdOrUsername}`;
  }
};

export const getPersonDisplayNameSync = (item: SaleOffer | BuyRequest): string => {
  const userId = getPersonId(item);
  return `User ${userId}`;
};

export const getPrice = (item: SaleOffer | BuyRequest): string => {
  return item.sharePrice;
};

export const getQuantity = (item: SaleOffer | BuyRequest): string => {
  return item.numberOfShares;
};

import { getCurrentTimezone } from '../config/settings';
import { formatCurrency as formatCurrencyWithSettings, formatNumber as formatNumberWithSettings } from './formatUtils';

export const getDate = (item: SaleOffer | BuyRequest): string => {
  const dateField = isSaleOffer(item) ? item.timestamp : item.requestTimestamp;
  const date = new Date(dateField);
  const timezone = getCurrentTimezone();
  
  // Use YYYY-MM-DD format consistent with settings
  const year = date.toLocaleDateString('en-CA', { timeZone: timezone, year: 'numeric' });
  const month = date.toLocaleDateString('en-CA', { timeZone: timezone, month: '2-digit' });
  const day = date.toLocaleDateString('en-CA', { timeZone: timezone, day: '2-digit' });
  
  return `${year}-${month}-${day}`;
};

export const getId = (item: SaleOffer | BuyRequest): string => {
  return item.id;
};

export const calculateTotal = (price: string, quantity: string): string => {
  return (parseFloat(price) * parseInt(quantity)).toFixed(2);
};

// Price comparison functions
export const isPriceMatch = (
  sourcePrice: string, 
  targetPrice: string, 
  matchType: 'buyers' | 'sellers'
): boolean => {
  const source = parseFloat(sourcePrice);
  const target = parseFloat(targetPrice);
  
  if (matchType === 'buyers') {
    // For buyers: target price should be >= source asking price
    return target >= source;
  } else {
    // For sellers: target price should be <= source max price
    return target <= source;
  }
};

// Generic matching logic
export const getMatches = <T extends SaleOffer | BuyRequest>(
  sourceItem: T,
  targetItems: (SaleOffer | BuyRequest)[],
  matchType: 'buyers' | 'sellers',
  excludeSelf = true
): (SaleOffer | BuyRequest)[] => {
  const sourcePrice = getPrice(sourceItem);
  const sourcePersonUsername = getPersonId(sourceItem);
  
  const matches = targetItems.filter(target => {
    // Exclude self if requested
    if (excludeSelf && getPersonId(target) === sourcePersonUsername) {
      return false;
    }
    
    // Check price compatibility
    return isPriceMatch(sourcePrice, getPrice(target), matchType);
  });
  
  // Sort matches appropriately
  if (matchType === 'buyers') {
    // Sort buyers by highest offer first
    return matches.sort((a, b) => parseFloat(getPrice(b)) - parseFloat(getPrice(a)));
  } else {
    // Sort sellers by lowest ask first
    return matches.sort((a, b) => parseFloat(getPrice(a)) - parseFloat(getPrice(b)));
  }
};

// Contact handling utilities
export interface ContactHandler {
  onSetBuyer: (user: User | null) => void;
  onSetSeller: (user: User | null) => void;
  onSetRelatedEntity: (id: string) => void;
  onShowMessageComposer: (show: boolean) => void;
  onNavigate?: (page: string) => void;
  getAllUsers: () => Promise<User[]>;
}

export const handleContactPerson = async (
  username: string,
  entityId: string,
  contactType: 'buyer' | 'seller',
  handlers: ContactHandler
): Promise<void> => {
  // Use React Router navigation instead of window.location.href
  if (handlers.onNavigate) {
    const contactParams = new URLSearchParams({
      recipient: username,
      subject: `Re: ${contactType === 'buyer' ? 'Buy Request' : 'Sale Offer'} - ${entityId}`,
      relatedEntity: entityId
    });
    
    handlers.onNavigate(`/inbox?${contactParams.toString()}`);
  } else {
    // Fallback to window.location.href if no navigation handler is provided
    const contactParams = new URLSearchParams({
      page: 'inbox',
      recipient: username,
      subject: `Re: ${contactType === 'buyer' ? 'Buy Request' : 'Sale Offer'} - ${entityId}`,
      relatedEntity: entityId
    });
    
    window.location.href = `?${contactParams.toString()}`;
  }
};

// Market analysis utilities
export const getMarketStatistics = (
  salesOffers: SaleOffer[],
  buyRequests: BuyRequest[]
) => {
  const validSales = salesOffers.filter(offer => offer.sharePrice && offer.numberOfShares);
  const validBuys = buyRequests.filter(request => request.sharePrice && request.numberOfShares);
  
  const salesPrices = validSales.map(offer => parseFloat(offer.sharePrice));
  const buyPrices = validBuys.map(request => parseFloat(request.sharePrice));
  
  return {
    highestBuyRequest: buyPrices.length > 0 ? Math.max(...buyPrices) : 0,
    lowestSalesOffer: salesPrices.length > 0 ? Math.min(...salesPrices) : Infinity,
    averageSalePrice: salesPrices.length > 0 ? salesPrices.reduce((a, b) => a + b, 0) / salesPrices.length : 0,
    averageBuyPrice: buyPrices.length > 0 ? buyPrices.reduce((a, b) => a + b, 0) / buyPrices.length : 0,
    totalSalesVolume: validSales.reduce((total, offer) => total + parseInt(offer.numberOfShares), 0),
    totalBuyVolume: validBuys.reduce((total, request) => total + parseInt(request.numberOfShares), 0),
    hasMatchingOpportunities: buyPrices.length > 0 && salesPrices.length > 0 && Math.max(...buyPrices) >= Math.min(...salesPrices)
  };
};

// Validation utilities
export const validateOffer = (offer: Partial<SaleOffer>): string[] => {
  const errors: string[] = [];
  
  if (!offer.sharePrice || isNaN(parseFloat(offer.sharePrice)) || parseFloat(offer.sharePrice) <= 0) {
    errors.push('Valid share price is required');
  }
  
  if (!offer.numberOfShares || isNaN(parseInt(offer.numberOfShares)) || parseInt(offer.numberOfShares) <= 0) {
    errors.push('Valid number of shares is required');
  }
  
  if (!offer.seller || offer.seller.trim().length === 0) {
    errors.push('Seller name is required');
  }
  
  return errors;
};

export const validateBuyRequest = (request: Partial<BuyRequest>): string[] => {
  const errors: string[] = [];
  
  if (!request.sharePrice || isNaN(parseFloat(request.sharePrice)) || parseFloat(request.sharePrice) <= 0) {
    errors.push('Valid share price is required');
  }
  
  if (!request.numberOfShares || isNaN(parseInt(request.numberOfShares)) || parseInt(request.numberOfShares) <= 0) {
    errors.push('Valid number of shares is required');
  }
  
  if (!request.requester || request.requester.trim().length === 0) {
    errors.push('Requester name is required');
  }
  
  return errors;
};

// Formatting utilities
export const formatCurrency = (amount: string | number): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return formatCurrencyWithSettings(num);
};

export const formatNumber = (amount: string | number): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return formatNumberWithSettings(num);
};

// Permissions utilities
export const canUserDeleteOffer = (offer: SaleOffer, user: User): boolean => {
  // Admin and SuperAdmin can delete any offer
  if (user.isAdmin || user.isSuperAdmin) {
    return true;
  }
  
  // Regular users can only delete their own offers
  return offer.seller === user.id;
};

export const canUserDeleteRequest = (request: BuyRequest, user: User): boolean => {
  // Admin and SuperAdmin can delete any request
  if (user.isAdmin || user.isSuperAdmin) {
    return true;
  }
  
  // Regular users can only delete their own requests
  return request.requester === user.id;
};

export const canUserContactForOffer = (offer: SaleOffer, currentUser: User): boolean => {
  return offer.seller === currentUser.id;
};

export const canUserContactForRequest = (request: BuyRequest, currentUser: User): boolean => {
  return request.requester === currentUser.id;
}; 